/*
 * TWC events — the browser half of the events index and the event post.
 *
 * Two custom elements, one file, because both surfaces render the same
 * details table and therefore need the same copy button:
 *
 *   <twc-events-index>  location filter · nearby sort · previous-events
 *                       pagination · copy button
 *   <twc-event-post>    copy button
 *
 * Everything here is an ENHANCEMENT. With no JavaScript the page still works:
 * every upcoming event Liquid could reach shows, every previous event it could
 * reach shows, the nearby button and the pagination stay hidden (the Liquid
 * renders them hidden and this file reveals them), and the discount code is on
 * the page as selectable text.
 *
 * THE ISLAND IS THE SECOND READER. The server draws both groups from the
 * engine's content digest — the complete upcoming set, the 60 newest past
 * events and the true archive total — so first paint is already correct.
 * This file has two jobs:
 *
 *   1. REACH PAST 60. Liquid stops at ~50 metaobject entries and the digest
 *      carries 60 past events, while the archive holds ~303. The complete
 *      Storefront read (assets/twc-events-source.js) is what lets the pager
 *      walk beyond the pages the server could render. `data-past-total` says
 *      how many pages really exist, so the page numbers never grow under the
 *      member; a page whose cards have not arrived yet is drawn disabled.
 *   2. RECONCILE. Server page 1 and island page 1 are the same list, so the
 *      island compares handles IN ORDER and touches nothing when they match.
 *      A longer complete list is APPENDED after the server's cards, which keep
 *      their place; only a real disagreement redraws a group.
 *
 * The read is EAGER, on load: both groups sit below the fold, so waiting for a
 * scroll would delay the deep pages until the member is already reading. When
 * the read fails the module answers null and this file does NOTHING — the
 * server's markup stays on screen, filterable and paginated.
 *
 * Geolocation arithmetic lives in assets/twc-geo.js (window.TwcGeo). An event
 * with no coordinates sorts last and is NEVER hidden — a live-stream event has
 * no place on the map but is still an event a member can join.
 *
 * One IIFE, idempotent under duplicate script tags (both sections load it, and
 * a page could hold both). Every listener is bound to an AbortController that
 * disconnectedCallback() aborts.
 */
(function () {
  'use strict';

  if (customElements.get('twc-events-index')) return;

  var COPY_RESET_MS = 2000;

  /* ---- 1. locale strings ------------------------------------------------ */

  // Shopify HTML-escapes every theme translation on output (only *_html keys
  // skip it), so "Date & Time" arrives in the JSON blob as "Date &amp; Time".
  // The island renders via textContent, which would show the entity literally.
  // Decode once at parse time — the twc-account-api.js precedent.
  var entityEl = null;
  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    if (!entityEl) entityEl = document.createElement('textarea');
    entityEl.innerHTML = str;
    return entityEl.value;
  }

  // The server digest renders rich-text summaries through Liquid's
  // `metafield_text`. Shopify can leave that first paint with a literal
  // apostrophe entity (for example, `&#39;`). Decode only the text node and
  // write it back through textContent, so no source markup can become HTML.
  function decodeServerSummaries(host) {
    host.querySelectorAll('.twc-events__banner-text, .twc-community__card-text').forEach(function (node) {
      var decoded = decodeEntities(node.textContent);
      if (decoded !== node.textContent) node.textContent = decoded;
    });
  }

  function strings(host) {
    if (host._twcStrings) return host._twcStrings;
    var blob = {};
    var tag = host.querySelector('script[data-twc-i18n]');
    if (tag) {
      try {
        blob = JSON.parse(tag.textContent) || {};
      } catch (error) {
        blob = {};
      }
    }
    for (var key in blob) {
      if (Object.prototype.hasOwnProperty.call(blob, key)) blob[key] = decodeEntities(blob[key]);
    }
    host._twcStrings = blob;
    return blob;
  }

  function t(host, key, fallback) {
    var value = strings(host)[key];
    return value == null || value === '' ? fallback : value;
  }

  /* ---- 2. the clipboard ------------------------------------------------- */

  /** Older browsers, and any page served over plain http, have no async
   *  clipboard. The hidden-textarea fallback is the only thing that works
   *  there, and it must run inside the click handler to keep the user gesture.
   */
  function legacyCopy(text) {
    var field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', 'readonly');
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();

    var copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    document.body.removeChild(field);
    return copied;
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(
        function () {
          return true;
        },
        function () {
          return legacyCopy(text);
        }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  /** Speak the confirmation. The visible "Copied" chip lives INSIDE the copy
   *  button, where the button's own aria-label flattens it out of the
   *  accessibility tree, so the snippet renders a sibling live region and this
   *  writes the chip's own words into it. Clearing on reset is what lets a
   *  second copy announce again.
   */
  function announceCopy(button, copied) {
    var parent = button.parentNode;
    var live = parent ? parent.querySelector('[data-twc-copy-status]') : null;
    if (!live) return;
    var chip = button.querySelector('.twc-community__copy-done');
    live.textContent = copied && chip ? chip.textContent.trim() : '';
  }

  /** One delegated handler serves every copy button under the host. */
  function bindCopy(host) {
    host.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest('[data-twc-copy]');
        if (!button || !host.contains(button)) return;

        event.preventDefault();
        var value = button.getAttribute('data-twc-copy') || '';
        if (!value) return;

        copyToClipboard(value).then(function (copied) {
          if (!copied) return;
          button.setAttribute('data-twc-copied', 'true');
          announceCopy(button, true);
          var timer = window.setTimeout(function () {
            button.setAttribute('data-twc-copied', 'false');
            announceCopy(button, false);
          }, COPY_RESET_MS);
          host._twcTimers.push(timer);
        });
      },
      { signal: host._twcController.signal }
    );
  }

  function clearTimers(host) {
    (host._twcTimers || []).forEach(function (timer) {
      window.clearTimeout(timer);
    });
    host._twcTimers = [];
  }

  /* ---- 3. the events index --------------------------------------------- */

  var CHEVRON_PREV =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<g transform="translate(4.25 2.25)">' +
    '<path d="M5.75 10.75L0.75 5.75L5.75 0.75" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</g></svg>';

  var CHEVRON_NEXT =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<g transform="translate(4.25 2.25)">' +
    '<path d="M0.75 0.75L5.75 5.75L0.75 10.75" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</g></svg>';

  /** "Page 3". The section passes the translation with `__n__` where the
   *  number goes; a translation that lost its placeholder still reads well. */
  function pageLabel(host, page) {
    var template = t(host, 'page_number', '');
    if (!template) return String(page);
    if (template.indexOf('__n__') !== -1) return template.replace('__n__', String(page));
    return template + ' ' + page;
  }

  /** The numbers the board draws: first, a window around the current page,
   *  last, and an ellipsis wherever the run breaks. */
  function pageList(current, total) {
    var wanted = [1, total, current - 1, current, current + 1];
    var pages = [];
    wanted
      .filter(function (page) {
        return page >= 1 && page <= total;
      })
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (page) {
        if (pages[pages.length - 1] !== page) pages.push(page);
      });

    var out = [];
    pages.forEach(function (page, index) {
      if (index > 0 && page - pages[index - 1] > 1) out.push(null);
      out.push(page);
    });
    return out;
  }

  /* ---- complete Storefront API collection ------------------------------- */

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null && text !== '') node.textContent = text;
    return node;
  }

  function append(parent, child) {
    if (child) parent.appendChild(child);
    return child;
  }

  /* ---- reconciling against what the server already drew ------------------ */

  /** assets/twc-dom-reconcile.js owns the compare-handles-in-order rule for
   *  both community indexes. Read lazily: this file also serves the event POST
   *  page, which mounts no list and loads no reconciler. */
  function reconciler() {
    return window.TwcListReconcile;
  }

  /** Every field, every date label and every split rule comes from
   *  assets/twc-events-source.js. This file owns only the DOM it writes, so
   *  the rebuilt cards and the Liquid-rendered ones cannot drift apart. */
  function source() {
    return window.TwcEventsSource;
  }

  function imageSrc(url, width) {
    return source().imageUrl(url, width);
  }

  function responsiveImage(event, title, widths, sizes, eager) {
    if (!event.heroImageUrl) return null;
    var img = element('img');
    img.src = imageSrc(event.heroImageUrl, widths[0]);
    img.srcset = widths
      .map(function (width) {
        return imageSrc(event.heroImageUrl, width) + ' ' + width + 'w';
      })
      .join(', ');
    img.sizes = sizes;
    img.alt = title;
    img.width = event.heroImageWidth || widths[widths.length - 1];
    img.height = event.heroImageHeight || Math.round(img.width * 0.8);
    img.loading = eager ? 'eager' : 'lazy';
    if (eager) img.setAttribute('fetchpriority', 'high');
    return img;
  }

  function dateBadge(event) {
    if (!event.badgeMonth && !event.badgeDay) return null;
    var badge = element('p', 'twc-community__date-badge twc-events__badge');
    append(badge, element('span', 'twc-community__date-badge-month', event.badgeMonth));
    append(badge, element('span', 'twc-community__date-badge-day', event.badgeDay));
    return badge;
  }

  function translated(host, key, token, value) {
    return t(host, key, '').replace(token, value);
  }

  function detailsRow(label, value) {
    if (!value) return null;
    var row = element('div', 'twc-community__row');
    append(row, element('dt', 'twc-community__row-label', label));
    append(row, element('dd', 'twc-community__row-value', value));
    return row;
  }

  /** Same rule as the Liquid snippet: a purely numeric price renders as
   *  money with the store's currency symbol (read once from any
   *  server-rendered details table); words ("Various", "Free") verbatim. */
  var currencySymbol = null;
  function money(value) {
    if (!value) return value;
    var text = String(value).trim();
    if (!/^\d+([.,]\d+)?$/.test(text)) return text;
    if (currencySymbol == null) {
      var table = document.querySelector('[data-twc-currency]');
      currencySymbol = (table && table.getAttribute('data-twc-currency')) || '$';
    }
    return currencySymbol + text;
  }

  function copyIcon() {
    var wrapper = document.createElement('span');
    wrapper.innerHTML =
      '<svg class="twc-community__copy-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M13.3334 5.33337H6.66671C5.93033 5.33337 5.33337 5.93033 5.33337 6.66671V13.3334C5.33337 14.0698 5.93033 14.6667 6.66671 14.6667H13.3334C14.0698 14.6667 14.6667 14.0698 14.6667 13.3334V6.66671C14.6667 5.93033 14.0698 5.33337 13.3334 5.33337Z" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M2.66671 10.6667C1.93337 10.6667 1.33337 10.0667 1.33337 9.33337V2.66671C1.33337 1.93337 1.93337 1.33337 2.66671 1.33337H9.33337C10.0667 1.33337 10.6667 1.93337 10.6667 2.66671" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    return wrapper.firstChild;
  }

  function copyCheck() {
    var wrapper = document.createElement('span');
    wrapper.innerHTML =
      '<svg class="twc-community__copy-check" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M2.5 8.5L6.5 12.5L13.5 4.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
      '</svg>';
    return wrapper.firstChild;
  }

  /** The discount row of snippets/twc-event-details-table.liquid, rebuilt:
   *  the code, the copy button (its icon, its "Copied" chip and the sibling
   *  live region), then the maroon savings chip. The button keeps the snippet's
   *  `id` so a rebuilt card and a server-rendered one are the same node. */
  function discountRow(host, event) {
    var code = event.discountCode;
    if (!code) return null;
    var row = element('div', 'twc-community__row');
    append(row, element('dt', 'twc-community__row-label', t(host, 'row_discount', 'Discount code')));
    var value = element('dd', 'twc-community__row-value twc-event-details__discount');
    append(value, element('span', 'twc-event-details__code', code));
    var button = element('button', 'twc-community__copy twc-event-details__copy');
    button.type = 'button';
    button.dataset.twcCopy = code;
    button.dataset.twcCopied = 'false';
    button.setAttribute('aria-label', translated(host, 'copy_label', '__value__', code));
    button.id = 'twc-event-copy-' + event.handle;
    append(button, copyIcon());
    append(button, copyCheck());
    var done = element('span', 'twc-community__copy-done', t(host, 'copy_done', 'Copied'));
    done.setAttribute('aria-hidden', 'true');
    append(button, done);
    append(value, button);
    var live = element('span', 'visually-hidden');
    live.setAttribute('role', 'status');
    live.setAttribute('data-twc-copy-status', '');
    append(value, live);
    if (event.discountPercent) {
      // Same rule as the Liquid snippet: a pure number renders the designed
      // "Members save n%" template; the M2 import's free-text savings
      // ("20% or an exclusive Members-only dram") renders VERBATIM.
      var savings = String(event.discountPercent).trim();
      var chipText = /^\d+$/.test(savings)
        ? translated(host, 'members_save', '__percent__', savings)
        : savings;
      append(
        value,
        element(
          'span',
          'twc-community__chip twc-community__chip--members twc-event-details__chip',
          chipText
        )
      );
    }
    append(row, value);
    return row;
  }

  /** Same seven rows, same order, same hide-when-empty rule as the snippet:
   *  Location · Date & Time · Price · Member price · Discount code · Country ·
   *  Age restriction. No rows at all means no table and no stray hairline. */
  function eventDetails(host, event) {
    var rows = [
      detailsRow(t(host, 'row_location', 'Location'), event.locationLabel),
      detailsRow(t(host, 'row_datetime', 'Date & Time'), event.datetimeLabel),
      detailsRow(t(host, 'row_price', 'Price'), money(event.regularPrice)),
      detailsRow(t(host, 'row_member_price', 'Member price'), money(event.memberPrice)),
      discountRow(host, event),
      detailsRow(t(host, 'row_country', 'Country'), event.country),
      detailsRow(t(host, 'row_age', 'Age restriction'), event.ageRestriction),
    ].filter(Boolean);
    if (!rows.length) return null;
    var table = element('dl', 'twc-community__table twc-event-details');
    rows.forEach(function (row) {
      append(table, row);
    });
    return table;
  }

  /** The card's photo, as the click-through convention wants it: a link the
   *  keyboard skips when the event has a page, an ordinary box when it has
   *  none. Mirrors sections/twc-events-index.liquid node for node. */
  function mediaBox(className, url) {
    if (!url) return element('div', className);
    var media = element('a', className);
    media.href = url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    return media;
  }

  /** The card's one tab stop: the title, wrapped in the only link an assistive
   *  technology is offered for the event page. */
  function cardTitle(tag, className, title, url) {
    var heading = element(tag, className);
    if (url) {
      var link = element('a', 'twc-events__title-link', title);
      link.href = url;
      heading.appendChild(link);
    } else {
      heading.textContent = title;
    }
    return heading;
  }

  function upcomingCard(host, event, eager) {
    var title = event.title;
    var card = element('li', 'twc-events__banner twc-community--on-dark');
    card.setAttribute('data-twc-event', '');
    card.dataset.twcHandle = event.handle;
    card.dataset.twcLocation = event.filterKey;
    // An event with no place on the map keeps its slot and sorts last under
    // "nearby" — a live stream is still an event a member can join.
    if (event.lat !== null && event.lng !== null) {
      card.dataset.twcLat = String(event.lat);
      card.dataset.twcLng = String(event.lng);
    }

    var media = mediaBox('twc-events__banner-media', event.url);
    append(media, responsiveImage(event, title, [720, 1100, 1440], '(min-width: 750px) 50vw, 100vw', eager));
    append(media, dateBadge(event));
    append(card, media);

    var body = element('div', 'twc-events__banner-body');
    var head = element('div', 'twc-events__banner-head');
    if (event.eventType) {
      append(head, element('p', 'twc-community__eyebrow twc-events__eyebrow', event.eventType));
    }
    if (title) append(head, cardTitle('h3', 'twc-events__banner-title', title, event.url));
    var description = source().truncate(event.description, 220);
    if (description) append(head, element('p', 'twc-events__banner-text', description));
    append(body, head);
    append(body, eventDetails(host, event));

    var actions = element('div', 'twc-events__actions');
    if (event.ctaUrl) {
      var primary = element(
        'a',
        'twc-events__cta twc-events__cta--gold',
        event.registerUsingFacebook
          ? t(host, 'register', 'Register')
          : event.usesFacebookFallback
            ? t(host, 'view_on_facebook', 'View event on Facebook')
            : event.ctaLabel || t(host, 'primary_cta', 'Go to event')
      );
      primary.href = event.ctaUrl;
      append(actions, primary);
    }
    if (event.url) {
      // A visible duplicate of the title link, so the keyboard skips it and
      // one accessible link per card survives.
      var details = element('a', 'twc-events__cta twc-events__cta--ghost', t(host, 'more_details', 'More details'));
      details.href = event.url;
      details.tabIndex = -1;
      details.setAttribute('aria-hidden', 'true');
      append(actions, details);
    }
    append(body, actions);
    append(card, body);
    return card;
  }

  function pastCard(host, event) {
    var title = event.title;
    var card = element('li', 'twc-community__card twc-events__past-card');
    card.setAttribute('data-twc-past-card', '');
    card.dataset.twcHandle = event.handle;
    var image = responsiveImage(event, title, [480, 720, 960], '(min-width: 750px) 33vw, 100vw', false);
    if (image) {
      // A passed event keeps its inert chip instead of a CTA, but the photo
      // and the title still reach the event page.
      var media = mediaBox('twc-community__card-media twc-events__past-media', event.url);
      append(media, image);
      append(card, media);
    }
    var body = element('div', 'twc-community__card-body twc-events__past-body');
    var head = element('div', 'twc-community__card-head');
    var region = event.eventType || event.locationLabel;
    if (region) append(head, element('p', 'twc-community__eyebrow twc-events__eyebrow', region));
    if (title) {
      append(head, cardTitle('h3', 'twc-community__card-title twc-events__past-title', title, event.url));
    }
    append(body, head);
    var description = source().truncate(event.description, 160);
    if (description) append(body, element('p', 'twc-community__card-text', description));
    var actions = element('div', 'twc-community__card-actions');
    append(
      actions,
      element(
        'span',
        'twc-community__chip twc-community__chip--passed twc-events__passed',
        t(host, 'passed', 'Event passed')
      )
    );
    append(body, actions);
    append(card, body);
    return card;
  }

  class TwcEventsIndex extends HTMLElement {
    connectedCallback() {
      this._twcController = new AbortController();
      this._twcTimers = [];

      this.filter = this.querySelector('[data-twc-filter]');
      this.nearby = this.querySelector('[data-twc-nearby]');
      this.status = this.querySelector('[data-twc-status]');
      this.empty = this.querySelector('[data-twc-empty]');
      this.list = this.querySelector('[data-twc-upcoming-list]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-twc-event]'));
      this.pastCards = Array.prototype.slice.call(this.querySelectorAll('[data-twc-past-card]'));
      this.pagination = this.querySelector('[data-twc-pagination]');
      this.upcomingGroup = this.querySelector('[data-twc-upcoming-group]');
      this.pastGroup = this.querySelector('[data-twc-past-group]');
      this.controls = this.querySelector('[data-twc-controls]');
      this.locationField = this.querySelector('[data-twc-location-field]');
      this.pastList = this.querySelector('[data-twc-past-list]');
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 6;
      // The TRUE size of the past archive, from the digest's own count — the
      // server could only draw its newest 60. 0 means "what you see is all
      // there is", which is what the no-digest fallback renders.
      this.pastTotal = parseInt(this.getAttribute('data-past-total'), 10) || 0;
      this.page = 1;

      decodeServerSummaries(this);
      bindCopy(this);
      this.bindFilter();
      this.bindNearby();
      this.setUpPagination();
      this.loadCompleteIndex();
    }

    disconnectedCallback() {
      if (this._twcController) this._twcController.abort();
      clearTimers(this);
    }

    /* -- location filter -- */

    /** EAGER on load, never gated on scroll. Both groups sit below the fold,
     *  so the complete list has to be in hand BEFORE a member scrolls to it —
     *  an IntersectionObserver would show them the 50-entry Liquid slice first
     *  and then swap it under their eyes. */
    loadCompleteIndex() {
      var api = window.TwcEventsSource;
      if (!api) return;
      var host = this;
      // ONE reconcile, against the COMPLETE read. A non-final cursor page is
      // id-ordered and can miss the server's newest-by-date cards entirely —
      // reconciling it would wipe the banner and rebuild the archive, then put
      // them back a round trip later: the exact swap this island must never make.
      api.load(api.options(this))
        .then(function (model) {
          // null = the read failed or no token is configured. The Liquid slice
          // stays exactly as the server drew it; nothing here blanks it out.
          if (!model || host._twcController.signal.aborted) return;
          host.replaceIndex(model);
        })
        .catch(function (error) {
          console.warn('The complete events index could not be applied.', error);
        });
    }

    replaceIndex(model) {
      var host = this;
      var patch = reconciler();
      if (!patch) return;
      var now = Date.now();
      var upcoming = model.upcoming(now);
      // The archive is what the pager walks and the server drew its newest 60.
      // Those cards keep their places; the rest of the collection follows them.
      var past = patch.orderedLike(model.past(now), patch.handlesOf(this.pastList));

      var shell = this.closest('.twc-events');
      if (shell) shell.hidden = model.events.length === 0;

      // The first banner photo that renders is this page's LCP element, so it
      // loads eagerly at high priority. A counter, not the array index: the
      // first event may carry no photo at all. An APPEND never claims it — the
      // card the server drew already holds it.
      var eagerUsed = false;
      patch.sync(this.list, upcoming, function (event, index, appended) {
        var eager = !appended && !eagerUsed && Boolean(event.heroImageUrl);
        if (eager) eagerUsed = true;
        return upcomingCard(host, event, eager);
      });
      patch.sync(this.pastList, past, function (event) {
        return pastCard(host, event);
      });

      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-twc-event]'));
      this.pastCards = Array.prototype.slice.call(this.querySelectorAll('[data-twc-past-card]'));
      if (this.upcomingGroup) this.upcomingGroup.hidden = !upcoming.length;
      if (this.pastGroup) this.pastGroup.hidden = !past.length;
      this.rebuildLocations(upcoming);
      this.applyFilter();
      // The member may already be past page 1 when the complete read lands —
      // keep their place, clamped to pages whose cards are actually here (a
      // stale digest count can promise more pages than the read returned).
      var page = this.page || 1;
      this.refreshPagination();
      var loadedPages = Math.max(1, Math.ceil(this.pastCards.length / this.perPage));
      var target = Math.min(page, loadedPages);
      if (this.pagination && target > 1) this.showPage(target, false);
    }

    rebuildLocations(events) {
      if (!this.filter) return;
      var selected = this.filter.value;
      var locations = {};
      events.forEach(function (event) {
        if (event.filterLabel) locations[event.filterKey] = event.filterLabel;
      });
      var options = Object.keys(locations).sort(function (a, b) {
        return locations[a].localeCompare(locations[b]);
      });
      this.filter.textContent = '';
      var all = element('option', '', t(this, 'all_locations', 'All locations'));
      all.value = '';
      append(this.filter, all);
      options.forEach(function (key) {
        var option = element('option', '', locations[key]);
        option.value = key;
        append(this.filter, option);
      }, this);
      this.filter.value = options.indexOf(selected) === -1 ? '' : selected;
      var showFilters = this.dataset.showFilters === 'true' && events.length > 0;
      if (this.controls) this.controls.hidden = !showFilters;
      if (this.locationField) this.locationField.hidden = options.length <= 1;
    }

    bindFilter() {
      if (!this.filter) return;
      this.filter.addEventListener(
        'change',
        this.applyFilter.bind(this),
        { signal: this._twcController.signal }
      );
    }

    applyFilter() {
      var wanted = this.filter ? this.filter.value : '';
      var shown = 0;

      this.cards.forEach(function (card) {
        var match = !wanted || card.getAttribute('data-twc-location') === wanted;
        card.hidden = !match;
        if (match) shown += 1;
      });

      if (this.empty) this.empty.hidden = shown > 0;
    }

    /* -- nearby sort -- */

    bindNearby() {
      if (!this.nearby) return;
      if (!window.TwcGeo || !window.TwcGeo.isSupported()) return;

      this.nearby.hidden = false;
      this.nearby.addEventListener(
        'click',
        this.sortByDistance.bind(this),
        { signal: this._twcController.signal }
      );
    }

    say(message) {
      if (this.status) this.status.textContent = message || '';
    }

    sortByDistance() {
      var host = this;
      if (!window.TwcGeo || !this.list) return;

      this.nearby.setAttribute('aria-busy', 'true');
      this.say(t(this, 'geo_busy', ''));

      window.TwcGeo.requestPosition().then(
        function (position) {
          host.nearby.removeAttribute('aria-busy');

          var ordered = window.TwcGeo.haversineSort(host.cards, position);
          var fragment = document.createDocumentFragment();
          ordered.forEach(function (entry) {
            fragment.appendChild(entry.item);
          });
          host.list.appendChild(fragment);
          host.cards = ordered.map(function (entry) {
            return entry.item;
          });

          host.say(t(host, 'geo_sorted', ''));
        },
        function (error) {
          host.nearby.removeAttribute('aria-busy');
          var key = error && error.code === 'denied' ? 'geo_denied' : 'geo_unavailable';
          host.say(t(host, key, ''));
        }
      );
    }

    /* -- previous-events pagination -- */

    /** How many pages the archive really has. The server draws its newest 60
     *  and says how many exist, so the numbers are right from first paint and
     *  never grow under the member when the complete read lands. */
    pageTotal() {
      var known = Math.max(this.pastCards.length, this.pastTotal || 0);
      return Math.max(1, Math.ceil(known / this.perPage));
    }

    /** A page is reachable once its cards are on the page. Pages past the
     *  server's slice stay disabled until the complete read fills them in. */
    pageIsLoaded(page) {
      return (page - 1) * this.perPage < this.pastCards.length;
    }

    setUpPagination() {
      if (!this.pagination || this.pageTotal() <= 1) return;
      this.pagination.hidden = false;
      this.showPage(1, false);
    }

    refreshPagination() {
      if (!this.pagination) return;
      this.pagination.textContent = '';
      this.pagination.hidden = true;
      this.pastCards.forEach(function (card) {
        card.hidden = false;
      });
      this.page = 1;
      this.setUpPagination();
    }

    showPage(page, moveFocus) {
      var total = this.pageTotal();
      this.page = Math.min(Math.max(page, 1), total);

      var first = (this.page - 1) * this.perPage;
      var last = first + this.perPage;
      this.pastCards.forEach(function (card, index) {
        card.hidden = index < first || index >= last;
      });

      this.renderPagination(total, moveFocus);
    }

    renderPagination(total, moveFocus) {
      var host = this;
      var nav = this.pagination;
      nav.textContent = '';

      var stepLabels = { prev: t(this, 'page_previous', 'Previous'), next: t(this, 'page_next', 'Next') };

      function step(kind, targetPage, disabled, markup) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'twc-community__page-step';
        button.setAttribute('aria-label', stepLabels[kind]);
        button.innerHTML = markup;
        if (disabled) {
          button.disabled = true;
        } else {
          button.addEventListener(
            'click',
            function () {
              host.showPage(targetPage, true);
            },
            { signal: host._twcController.signal }
          );
        }
        nav.appendChild(button);
      }

      step('prev', this.page - 1, this.page === 1, CHEVRON_PREV);

      pageList(this.page, total).forEach(function (page) {
        if (page === null) {
          var gap = document.createElement('span');
          gap.className = 'twc-events__page-gap';
          gap.setAttribute('aria-hidden', 'true');
          gap.textContent = '…';
          nav.appendChild(gap);
          return;
        }

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'twc-community__page';
        button.textContent = String(page);
        button.setAttribute('aria-label', pageLabel(host, page));
        if (page === host.page) {
          button.setAttribute('aria-current', 'page');
        }
        if (host.pageIsLoaded(page)) {
          button.addEventListener(
            'click',
            function () {
              host.showPage(page, true);
            },
            { signal: host._twcController.signal }
          );
        } else {
          // The number is honest about how far the archive goes; the button
          // waits for the complete read to bring those cards in.
          button.disabled = true;
        }
        nav.appendChild(button);
      });

      step('next', this.page + 1, this.page === total || !this.pageIsLoaded(this.page + 1), CHEVRON_NEXT);

      if (moveFocus) {
        var current = nav.querySelector('[aria-current="page"]');
        if (current) current.focus();
      }
    }
  }

  /* ---- 4. the event post ------------------------------------------------ */

  class TwcEventPost extends HTMLElement {
    connectedCallback() {
      this._twcController = new AbortController();
      this._twcTimers = [];
      bindCopy(this);
    }

    disconnectedCallback() {
      if (this._twcController) this._twcController.abort();
      clearTimers(this);
    }
  }

  customElements.define('twc-events-index', TwcEventsIndex);
  customElements.define('twc-event-post', TwcEventPost);
})();
